import { allVariables } from "./catalog";

export const VARIABLE_TOKEN_REGEX = /\{\{[^{}]*\}\}/g;
const SPLIT_TOKEN_REGEX = /(\{\{[^{}]*\}\}|\{[^{}]*\})/g;
const SINGLE_BRACE_TOKEN_REGEX = /\{[^{}]*\}/g;
const STARTS_WITH_VARIABLE_REGEX = /^\s*\{\{[^{}]*\}\}/;
const ENDS_WITH_VARIABLE_REGEX = /\{\{[^{}]*\}\}\s*$/;
const WORD_TOKEN_REGEX = /([A-Za-zÀ-ÿ_][A-Za-z0-9À-ÿ_]*)/g;

const VALID_VARIABLES = new Set(
  allVariables.map((variable) => String(variable?.value || "").trim()),
);

export const isVariableToken = (text) => /^\{\{[^{}]*\}\}$/.test(String(text || ""));
export const isSingleBraceToken = (text) =>
  /^\{[^{}]*\}$/.test(String(text || "")) && !isVariableToken(text);

const extractVariableName = (token) => String(token || "").slice(2, -2).trim();

export const getVariableTokenState = (token) => {
  if (isSingleBraceToken(token)) return "warning";
  if (!isVariableToken(token)) return "plain";

  const variableName = extractVariableName(token);
  if (!variableName) return "invalid";

  return VALID_VARIABLES.has(variableName) ? "valid" : "invalid";
};

const splitPlainSegmentByPotentialVariables = (segmentText) => {
  const text = String(segmentText || "");
  if (!text) return [];

  const pieces = [];
  let lastIndex = 0;

  text.replace(WORD_TOKEN_REGEX, (word, _group, offset) => {
    if (offset > lastIndex) {
      pieces.push({
        text: text.slice(lastIndex, offset),
        state: "plain",
      });
    }

    pieces.push({
      text: word,
      state: VALID_VARIABLES.has(word) ? "suggested" : "plain",
    });

    lastIndex = offset + word.length;
    return word;
  });

  if (lastIndex < text.length) {
    pieces.push({
      text: text.slice(lastIndex),
      state: "plain",
    });
  }

  return pieces;
};

export const splitTextByVariableTokens = (text) =>
  String(text || "")
    .split(SPLIT_TOKEN_REGEX)
    .flatMap((part) => {
      const state = getVariableTokenState(part);
      if (state !== "plain") return [{ text: part, state }];
      return splitPlainSegmentByPotentialVariables(part);
    })
    .map((part, index) => ({
      key: `${part.text}-${part.state}-${index}`,
      text: part.text,
      state: part.state,
    }));

const getTokenMatches = (text, regex) => {
  const source = String(text || "");
  const matches = [];
  regex.lastIndex = 0;

  let match;
  while ((match = regex.exec(source)) !== null) {
    matches.push({
      token: match[0],
      index: match.index,
    });
  }

  return matches;
};

export const analyzeVariableText = (text) => {
  const source = String(text || "");
  const parts = splitTextByVariableTokens(source);

  const variableTokens = getTokenMatches(source, VARIABLE_TOKEN_REGEX).map((item) => ({
    ...item,
    state: getVariableTokenState(item.token),
  }));
  const invalidVariableTokens = variableTokens.filter((item) => item.state === "invalid");
  const warningTokens = getTokenMatches(source, SINGLE_BRACE_TOKEN_REGEX).filter((item) => {
    const previousChar = source[item.index - 1];
    const nextChar = source[item.index + item.token.length];

    if (previousChar === "{" || nextChar === "}") return false;

    return getVariableTokenState(item.token) === "warning";
  });

  return {
    hasInvalidVariables: invalidVariableTokens.length > 0,
    hasWarningTokens: warningTokens.length > 0,
    suggestedVariables: Array.from(
      new Set(parts.filter((part) => part.state === "suggested").map((part) => part.text)),
    ),
    startsWithVariable: STARTS_WITH_VARIABLE_REGEX.test(source),
    endsWithVariable: ENDS_WITH_VARIABLE_REGEX.test(source),
    invalidVariableTokens: invalidVariableTokens.map((item) => item.token),
    warningTokens: warningTokens.map((item) => item.token),
  };
};

export const convertSuggestedVariableToToken = (text, variableName) =>
  splitTextByVariableTokens(text)
    .map((part) =>
      part.state === "suggested" && part.text === variableName
        ? `{{${variableName}}}`
        : part.text,
    )
    .join("");
